import { Stack, usePathname, useSegments } from "expo-router";
import Head from "expo-router/head";
import { useEffect, useMemo, useState } from "react";
import { View } from "react-native";

import { ErrorBoundary } from "../../components/ErrorBoundary";
import { JudgeShell, type JudgeIdentity, type JudgeNavKey } from "../../components/judge/JudgeShell";
import { ViewAsBanner } from "../../components/admin/ViewAsBanner";
import { isImpersonating } from "../../lib/impersonate";
import { JudgePortalGate } from "../../lib/JudgePortalGate";
import { subscribeToAvatarChanges } from "../../lib/avatarBus";
import { useAuth } from "../../lib/authContext";
import { getJudgeCompetitionSummary } from "../../lib/scoring";
import { supabase } from "../../lib/supabase";
import { PANE_THEMES } from "../../components/brand/paneThemes";

const THEME = PANE_THEMES.Judge;

function makeInitials(s: string | null | undefined): string {
  if (!s?.trim()) return "??";
  const parts = s.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function buildJudgeIdentity(
  user: ReturnType<typeof useAuth>["user"],
  avatarUrl: string | null,
): JudgeIdentity {
  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const fullName =
    typeof meta.full_name === "string" && meta.full_name.trim()
      ? (meta.full_name as string).trim()
      : user?.email?.split("@")[0] ?? "Judge";
  const organization =
    typeof meta.organization === "string" && (meta.organization as string).trim()
      ? (meta.organization as string).trim()
      : "";
  return {
    fullName,
    orgLine: organization,
    initials: makeInitials(fullName),
    avatarUrl,
  };
}

const titleCache = new Map<string, string>();

export default function JudgeLayout() {
  const segments = useSegments();
  const pathname = usePathname();
  const { user } = useAuth();

  const last = segments[segments.length - 1] ?? "";

  // Match the active competition id (excluding "new" or non-uuid sentinels).
  const compMatch = pathname.match(/\/judge\/competition\/([^\/]+)/);
  const competitionId = compMatch ? compMatch[1] : null;

  let activeKey: JudgeNavKey | undefined;
  let topbarTitle = "Assignments";

  if (last === "profile") {
    activeKey = "profile";
    topbarTitle = "Profile";
  } else if (last === "settings") {
    activeKey = "settings";
    topbarTitle = "Settings";
  } else if (pathname.startsWith("/judge/student/")) {
    activeKey = undefined;
    topbarTitle = "Candidate context";
  } else if (last === "[submissionId]" || (segments as string[]).includes("score")) {
    activeKey = undefined;
    topbarTitle = "Scoring submission";
  } else if (competitionId) {
    activeKey = undefined;
    // Title is derived async from the competition row below.
    topbarTitle = "Loading…";
  } else if (last === "judge" || last === "(judge)") {
    activeKey = "assignments";
    topbarTitle = "Assignments";
  } else {
    activeKey = "assignments";
    topbarTitle = "Assignments";
  }

  const [competitionTitle, setCompetitionTitle] = useState<string | undefined>(
    competitionId ? titleCache.get(competitionId) : undefined,
  );

  useEffect(() => {
    if (!competitionId) {
      setCompetitionTitle(undefined);
      return;
    }
    const cached = titleCache.get(competitionId);
    if (cached) setCompetitionTitle(cached);
    let cancelled = false;
    void getJudgeCompetitionSummary(competitionId).then((row) => {
      if (cancelled || !row?.name) return;
      titleCache.set(competitionId, row.name);
      setCompetitionTitle(row.name);
    });
    return () => {
      cancelled = true;
    };
  }, [competitionId]);

  // Avatar lives on user_profiles, not user_metadata. Fetch on user change and
  // subscribe to the avatar bus so an upload from the profile page propagates
  // to the topbar without a navigation.
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!user) {
      setAvatarUrl(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from("user_profiles")
        .select("avatar_url")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      const v = typeof data?.avatar_url === "string" && data.avatar_url.trim() ? data.avatar_url : null;
      setAvatarUrl(v);
    })();
    const unsub = subscribeToAvatarChanges((uid, url) => {
      if (uid === user.id) setAvatarUrl(url);
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [user]);

  const identity = useMemo(() => buildJudgeIdentity(user, avatarUrl), [user, avatarUrl]);

  const finalTitle = competitionId
    ? activeKey === undefined && (last === "[submissionId]" || (segments as string[]).includes("score"))
      ? "Scoring submission"
      : competitionTitle ?? "Loading…"
    : topbarTitle;

  return (
    <>
      <Head>
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <JudgePortalGate>
        <View style={{ flex: 1 }}>
          {isImpersonating() ? <ViewAsBanner theme={THEME} /> : null}
          <View style={{ flex: 1 }}>
            <JudgeShell topbarTitle={finalTitle} activeKey={activeKey} identity={identity}>
              <ErrorBoundary boundaryName="judge">
                <Stack
                  screenOptions={{
                    headerShown: false,
                    animation: "none",
                    contentStyle: { backgroundColor: THEME.pageBg },
                  }}
                />
              </ErrorBoundary>
            </JudgeShell>
          </View>
        </View>
      </JudgePortalGate>
    </>
  );
}
